import {Component, OnDestroy, OnInit} from '@angular/core';
import {Localization} from "../shared/enums/localization";
import {FormControl, Validators} from "@angular/forms";
import {distinctUntilChanged, filter, lastValueFrom, Subject, takeUntil} from "rxjs";
import {Router} from "@angular/router";
import {TwoFactorService} from "../shared/service/two-factor.service";
import {AuthorizationService} from "../shared/service/authorization.service";
import {LoginSuccessResponse} from "../shared/model/login-success-response";
import {EnvironmentType} from "../shared/enums/enums";
import {environment} from "../../../environments/environment";
import {RedirectService} from "../shared/service/redirect.service";
import {NgOtpInputConfig} from "ng-otp-input";

const OTP_LENGTH = 6;

@Component({
  selector: 'app-two-factor-auth',
  templateUrl: './two-factor-auth.component.html',
  styleUrls: ['./two-factor-auth.component.scss']
})
export class TwoFactorAuthComponent implements OnInit, OnDestroy {

  public readonly Localization = Localization;
  public isBusy = false;

  public readonly otpFieldConfig: NgOtpInputConfig = { length: OTP_LENGTH, letterCase: 'Upper'};
  public readonly otpField = new FormControl(null, [Validators.required, Validators.min(OTP_LENGTH), Validators.max(OTP_LENGTH)]);
  public readonly otp$ = this.otpField.valueChanges.pipe(
      filter(otp => otp.length === OTP_LENGTH),
      distinctUntilChanged(),
  );
  private destroyed = new Subject<TwoFactorAuthComponent>();
  public error?: Localization.TwoFactorOTPInvalidMessage
  public get otp(): string {
    return this.otpField.value;
  }
  constructor(
      private readonly router: Router,
      private readonly twoFactor: TwoFactorService,
      private readonly auth: AuthorizationService,
      private readonly redirect: RedirectService,
  ) {
  }

  async ngOnInit() {
    await this.ensureUserHasPreviouslyLoggedIn();

    this.otp$.pipe(
        takeUntil(this.destroyed),
    ).subscribe(async () => this.authenticateWithOTP())
  }

  public ngOnDestroy() {
    this.destroyed.next(this);
    this.destroyed.complete();
  }

  public async goBack() {
    this.twoFactor.reset();
    await this.router.navigate(['login']);
  }

  public async authenticateWithOTP() {
    delete this.error;
    this.isBusy = true;
    try {
      const response = await this.twoFactor.authenticateWith(this.otp).toPromise();
      await this.completeLogin(response);
    } catch (error) {
      await this.handleAuthenticationError(error);
    } finally {
      this.isBusy = false;
    }
  }


  private handleAuthenticationError(error) {
    switch (error.status) {
      case 400:
        return this.goBack();
      case 422:
        return this.error = Localization.TwoFactorOTPInvalidMessage;
      default:
        throw error;
    }
  }

  private async ensureUserHasPreviouslyLoggedIn() {
    if (!this.twoFactor.loginId) {
      await this.goBack();
    }
  }

  private async completeLogin(response: LoginSuccessResponse) {
    if (environment.environmentName === EnvironmentType.LOCAL) {
      this.auth.signInOnLocalEnv(response);
    } else {
      this.auth.signIn();
    }
    await this.redirect.restore('/dashboard');

  }
}
